/**
 * Facility scope for dashboards that can span more than one facility (issue #243).
 *
 * The Django session's `FACILITY_ID` always holds exactly one concrete facility — TECEncounters,
 * TECPatient and TECHR all read it with a bare `Facility.objects.get()` and would break on a
 * sentinel value. "All facilities" is therefore carried beside it as a separate mode, rendered
 * into the page by `get_facility_scope` in TECWeb/TECCore/view_master.py as
 * `window.facilityScope`, so it is available synchronously at first paint and needs no fetch.
 */

export type FacilityScopeMode = 'single' | 'all';

export interface FacilityScope {
  /** 'all' only when the user explicitly chose to span every facility. */
  mode: FacilityScopeMode;
  /** The concrete facility on the session, or null when none has been chosen yet. */
  facilityId: number | null;
}

/** What a page without a rendered scope falls back to: one facility, none chosen. */
export const DEFAULT_FACILITY_SCOPE: FacilityScope = {
  mode: 'single',
  facilityId: null,
};

function toFacilityId(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  // Django renders facility ids as strings in a few places (tenantContext.facilityId).
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/** Normalise whatever the page rendered into a scope, tolerating an older or absent payload. */
export function parseFacilityScope(raw: unknown): FacilityScope {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_FACILITY_SCOPE;
  }

  const candidate = raw as { mode?: unknown; facilityId?: unknown };

  return {
    // Anything other than an explicit 'all' is a single facility, matching the Django default.
    mode: candidate.mode === 'all' ? 'all' : 'single',
    facilityId: toFacilityId(candidate.facilityId),
  };
}

/** Read the scope the Django template rendered into the page. */
export function readFacilityScope(): FacilityScope {
  return parseFacilityScope((window as Window & { facilityScope?: unknown }).facilityScope);
}

/**
 * The facilities the Quality Dashboard should start with.
 *
 * An empty list is what the API reads as "every active facility" (`get_quality_dashboard` in
 * TECWeb/TECDashboard/views.py), so it serves as both the 'all' scope and the safe fallback
 * when no facility has been chosen yet — which is the behaviour every user got before #243.
 */
export function initialQualityFacilityIds(
  mode: FacilityScopeMode,
  facilityId: number | null,
): number[] {
  if (mode === 'all' || facilityId === null) {
    return [];
  }
  return [facilityId];
}
